import React from "react";
import NiceModal, {useModal} from "@ebay/nice-modal-react";
import {zodResolver} from "@hookform/resolvers/zod";
import {FormProvider, useForm} from "react-hook-form";
import {toast} from "sonner";
import {Dialog, DialogActions, DialogContent, DialogTitle} from "@mui/material";
import {Box, Colors, Container, IconAlert, IconButton, IconClose, Text} from "@bounce/shared/ui";
import {Button} from "../../ui/components";
import {useCreatePromo} from "../../apis/requests/promos/createPromo";
import {ReachPromo} from "../../apis/requests/promos/types";
import {useUpdatePromo} from "../../apis/requests/promos/updatePromo";
import {firstErrorMessage} from "../segments/segment-form/helpers";
import CommsCard from "./promo-form/CommsCard";
import DetailsCard from "./promo-form/DetailsCard";
import EligibilityCard from "./promo-form/EligibilityCard";
import TermsCard from "./promo-form/TermsCard";
import WindowCard from "./promo-form/WindowCard";
import {emptyPromoForm, formValuesToPayload, promoToFormValues, todayDateInput} from "./promo-form/helpers";
import {PromoFormValues, promoFormSchema} from "./promo-form/schema";

const DIALOG_MAX_WIDTH = 900;
const MATCHING_SUFFIX = "accounts match the saved segments";

const SECONDARY_BUTTON_SX = {
  borderColor: Colors.tertiary._300,
  color: Colors.grey._800,
  "&:hover": {borderColor: Colors.tertiary._400, backgroundColor: Colors.grey._100},
};

type Props = {
  promo?: ReachPromo;
};

const PromoDialog = NiceModal.create(({promo: existing}: Props) => {
  const isEdit = !!existing;
  const modal = useModal();
  const {mutateAsync: createAsync, isPending: isCreating} = useCreatePromo();
  const {mutateAsync: updateAsync, isPending: isUpdating} = useUpdatePromo();
  const isSaving = isCreating || isUpdating;
  const matchingAccountsCount = existing?.matching_accounts_count;

  const methods = useForm<PromoFormValues>({
    resolver: zodResolver(promoFormSchema),
    defaultValues: existing ? promoToFormValues(existing) : emptyPromoForm(todayDateInput()),
  });
  const {
    handleSubmit,
    formState: {isDirty, errors},
  } = methods;
  const issue = firstErrorMessage(errors);

  const onSubmit = handleSubmit(async (values) => {
    const payload = formValuesToPayload(values, todayDateInput());
    try {
      if (existing) {
        await updateAsync({id: existing.id, payload});
      } else {
        await createAsync(payload);
      }
      toast.success(`Promo "${payload.name}" ${isEdit ? "saved" : "created"}`);
      modal.hide();
    } catch {}
  });

  return (
    <Dialog
      open={modal.visible}
      onClose={() => modal.hide()}
      slotProps={{
        transition: {onExited: () => modal.remove()},
        paper: {
          sx: {
            borderRadius: "16px",
            maxWidth: DIALOG_MAX_WIDTH,
            width: "100%",
            backgroundColor: Colors.bw.white,
          },
        },
      }}
      maxWidth={false}
    >
      <DialogTitle sx={{p: "20px 24px", display: "flex", justifyContent: "space-between", alignItems: "center"}}>
        <Text size={20} variant="interDisplay600" sx={{color: Colors.grey._800}}>
          {isEdit ? "Edit promo" : "New promo"}
        </Text>
        <IconButton
          onClick={() => modal.hide()}
          size="small"
          sx={{color: Colors.grey._500, "&:hover": {backgroundColor: Colors.tertiary._200}}}
        >
          <IconClose width={20} height={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{p: "0 24px 24px"}}>
        <FormProvider {...methods}>
          <Box component="form" onSubmit={onSubmit} sx={{display: "flex", flexDirection: "column", gap: 2}}>
            <DetailsCard />
            <EligibilityCard />
            <TermsCard />
            <WindowCard />
            <CommsCard initialTemplates={existing?.templates ?? []} />
          </Box>
        </FormProvider>
      </DialogContent>
      <DialogActions
        sx={{p: "16px 24px", justifyContent: "space-between", borderTop: `1px solid ${Colors.tertiary._200}`}}
      >
        <Container row sx={{gap: 1.5, alignItems: "center", minHeight: 32}}>
          {issue ? (
            <Container row sx={{gap: 1, alignItems: "center"}}>
              <IconAlert width={18} height={18} color={Colors.red._900} />
              <Text size={13} sx={{color: Colors.red._900}}>
                {issue}
              </Text>
            </Container>
          ) : (
            matchingAccountsCount != null && (
              <Text size={13} sx={{color: Colors.grey._700}}>
                {`${matchingAccountsCount.toLocaleString()} ${MATCHING_SUFFIX}`}
              </Text>
            )
          )}
        </Container>
        <Container row sx={{gap: 1}}>
          <Button buttonType="outlined-primary" size="small" onClick={() => modal.hide()} sx={SECONDARY_BUTTON_SX}>
            Cancel
          </Button>
          <Button
            buttonType="contained-primary"
            size="small"
            onClick={onSubmit}
            disabled={isSaving || (isEdit && !isDirty)}
          >
            {isEdit ? "Apply changes" : "Add promo"}
          </Button>
        </Container>
      </DialogActions>
    </Dialog>
  );
});

export default PromoDialog;
